import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Building2, Save, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { MasterReadOnlySection, Field } from "./immeubleDossierDetail/MasterReadOnly";
import { PropertiesAccordion } from "./immeubleDossierDetail/PropertiesAccordion";
import { ConfirmArchive } from "./immeubleDossierDetail/Modals";
import { API, authH } from "@/api";


/**
 * Étape 2 (Immeuble) — Page indépendante de la fiche Master.
 *  HAUT   : bandeau Master en lecture seule (synchronisé via GET /masters/{id})
 *  MILIEU : champs globaux fiscaux éditables (collection immeuble_dossiers)
 *  BAS    : arborescence Property → Apartment → Tenant en accordéons CRUD
 *
 * Batch #14.221 — refactor oversized : sous-composants extraits dans ./immeubleDossierDetail/
 */
export default function ImmeubleDossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [master, setMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [showArchive, setShowArchive] = useState(false);

  // Dirty tracking
  const dirty = form && dossier ? Object.keys(form).some((k) => {
    const a = JSON.stringify(form[k] ?? null);
    const b = JSON.stringify(dossier[k] ?? null);
    return a !== b;
  }) : false;
  const confirmLeave = useUnsavedChanges(dirty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/immeuble-dossiers/${id}`, { headers: authH() });
      setDossier(r.data);
      setForm({
        neq: r.data.neq || "",
        accounting_type: r.data.accounting_type || "",
        fiscal_year_end: r.data.fiscal_year_end || "",
        tps_registered: !!r.data.tps_registered,
        tvq_registered: !!r.data.tvq_registered,
        incorporated: !!r.data.incorporated,
        holding_structure: r.data.holding_structure || "",
        notes: r.data.notes || "",
      });
      try {
        const m = await axios.get(`${API}/masters/${r.data.master_id}`, { headers: authH() });
        setMaster(m.data);
      } catch {
        setMaster(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
      navigate("/admin/immeuble-dossiers");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Batch #14.122 — Breadcrumb : Accueil › Immeubles › Dossiers Immeubles › Master <couple> › Dossier
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!dossier) return undefined;
    const couple = master?.couple_display_name || "(Master inconnu)";
    // Préfère NEQ s'il existe, sinon nombre de propriétés
    const label = dossier.neq
      ? `Immeuble · NEQ ${dossier.neq}`
      : `Immeuble · ${(dossier.properties || []).length} propriété(s)`;
    setTrail([
      { label: "Immeubles", href: "/admin/immeubles" },
      { label: "Dossiers Immeubles", href: "/admin/immeuble-dossiers" },
      { label: couple, href: `/admin/masters/${dossier.master_id}` },
      { label },
    ]);
    return () => setTrail(null);
  }, [dossier, master, setTrail]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        neq: form.neq || null,
        accounting_type: form.accounting_type || null,
        fiscal_year_end: form.fiscal_year_end || null,
        holding_structure: form.holding_structure || null,
        notes: form.notes || null,
      };
      const r = await axios.put(`${API}/immeuble-dossiers/${id}`, payload, { headers: authH() });
      setDossier(r.data);
      toast.success("Dossier immeuble sauvegardé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    try {
      await axios.delete(`${API}/immeuble-dossiers/${id}`, { headers: authH() });
      toast.success("Dossier immeuble archivé");
      navigate("/admin/immeuble-dossiers");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div className="text-center py-20"><Loader2 className="inline w-7 h-7 animate-spin text-slate-400" /></div>;
  }
  if (!dossier || !form) return null;

  const nProps = (dossier.properties || []).length;
  const nApts = (dossier.properties || []).reduce((s, p) => s + (p.apartments?.length || 0), 0);
  const nTenants = (dossier.properties || []).reduce(
    (s, p) => s + (p.apartments || []).reduce((ss, a) => ss + (a.tenants?.length || 0), 0),
    0,
  );

  return (
    <div className="space-y-5" data-testid="immeuble-dossier-detail">
      {/* Header navigation + archive */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/immeuble-dossiers"
          onClick={(e) => { if (!confirmLeave()) e.preventDefault(); }}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="immeuble-detail-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux dossiers immeubles
        </Link>
        <button
          onClick={() => setShowArchive(true)}
          className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
          data-testid="immeuble-detail-archive-btn"
        >
          <Trash2 className="w-3 h-3" /> Archiver ce dossier
        </button>
      </div>

      {/* Titre */}
      <div className="bg-gradient-to-br from-emerald-50 to-white border border-slate-200 rounded-2xl p-5" data-testid="immeuble-detail-header">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Dossier Immeuble — Déclaratif
              </h1>
              <div className="text-sm text-slate-600">
                {master?.couple_display_name || "(Master inconnu)"}
              </div>
              <div className="text-[11px] text-slate-400 font-mono mt-0.5">{dossier.id}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{nProps} propriété{nProps > 1 ? "s" : ""}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{nApts} logement{nApts > 1 ? "s" : ""}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{nTenants} locataire{nTenants > 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Créé le {new Date(dossier.created_at).toLocaleDateString("fr-CA")} par {dossier.created_by || "—"}
        </div>
      </div>

      {/* Section MASTER (lecture seule) */}
      <MasterReadOnlySection master={master} masterId={dossier.master_id} />

      {/* Section CHAMPS GLOBAUX FISCAUX (éditable) */}
      <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid="immeuble-detail-form">
        <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-600" /> Champs globaux du dossier (propres à ce dossier)
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="NEQ — Numéro d'Entreprise du Québec">
            <input
              type="text"
              value={form.neq}
              onChange={(e) => upd("neq", e.target.value)}
              maxLength={50}
              placeholder="1234567890"
              className="cc-input font-mono"
              data-testid="immeuble-neq"
            />
          </Field>
          <Field label="Type comptable">
            <input
              type="text"
              value={form.accounting_type}
              onChange={(e) => upd("accounting_type", e.target.value)}
              maxLength={100}
              placeholder="ex: personnel, société, fiducie…"
              className="cc-input"
              data-testid="immeuble-accounting-type"
            />
          </Field>
        </div>

        <Field label="Fin d'exercice fiscal" hint="Format MM-DD ou YYYY-MM-DD" className="mt-4">
          <input
            type="text"
            value={form.fiscal_year_end}
            onChange={(e) => upd("fiscal_year_end", e.target.value)}
            maxLength={10}
            placeholder="12-31"
            className="cc-input font-mono"
            data-testid="immeuble-fiscal-year-end"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={!!form.tps_registered}
              onChange={(e) => upd("tps_registered", e.target.checked)}
              data-testid="immeuble-tps"
            />
            Enregistré TPS
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={!!form.tvq_registered}
              onChange={(e) => upd("tvq_registered", e.target.checked)}
              data-testid="immeuble-tvq"
            />
            Enregistré TVQ
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={!!form.incorporated}
              onChange={(e) => upd("incorporated", e.target.checked)}
              data-testid="immeuble-incorporated"
            />
            Incorporé
          </label>
        </div>

        <Field label="Structure / Holding" hint="Description libre de la structure de détention" className="mt-4">
          <textarea
            rows={2}
            value={form.holding_structure}
            onChange={(e) => upd("holding_structure", e.target.value)}
            maxLength={500}
            placeholder="ex: Holding XYZ inc. → Société exploitante immobilière"
            className="cc-input"
            data-testid="immeuble-holding"
          />
        </Field>

        <Field label="Notes internes" hint="Visibles uniquement par l'admin (2000 chars max)" className="mt-4">
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => upd("notes", e.target.value)}
            maxLength={2000}
            className="cc-input"
            data-testid="immeuble-notes"
          />
        </Field>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 mt-5 border-t border-slate-100">
          <span className="text-[11px] text-slate-500">
            {dirty ? "⚠️ Modifications non sauvegardées" : "Tout est à jour"}
          </span>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="immeuble-detail-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      {/* Section PROPRIÉTÉS — accordéons éditables (Étape 3 : CRUD complet) */}
      <PropertiesAccordion
        dossierId={id}
        properties={dossier.properties || []}
        onChange={(updated) => setDossier(updated)}
      />

      {showArchive && (
        <ConfirmArchive
          onClose={() => setShowArchive(false)}
          onConfirm={archive}
        />
      )}

      <style>{`
        .cc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; }
        .cc-input:focus { border-color: #10b981; box-shadow: 0 0 0 1px #10b981; }
      `}</style>
    </div>
  );
}

